import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import type { Role } from "@/api/types";
import { useAuth } from "@/auth/AuthProvider";
import { homePath } from "@/auth/roles";
import { Loading } from "@/ui";

/** Everything below needs a signed-in person. Others are sent to the sign-in page. */
export function RequireAuth() {
  const { status } = useAuth();
  const location = useLocation();
  if (status === "loading") return <Loading fullscreen />;
  // The query string goes along, so /connect?code=… survives the round trip through sign-in.
  if (status === "signed-out") return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <Outlet />;
}

/**
 * Show the page only to the listed roles; anyone else goes to their own home.
 * This is for navigation only. The API enforces the same rule on every request.
 */
export function RequireRole({ roles, children }: { roles: readonly Role[]; children: ReactNode }) {
  const { user } = useAuth();
  if (!user) return null;
  if (!roles.includes(user.role)) return <Navigate to={homePath(user.role)} replace />;
  return <>{children}</>;
}
